import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import EditPostModal from './EditPostModal';
import RichText from './RichText';
import '../styles/PostCard.css';

const PostCard = ({ post, onLike, onComment, onPostUpdate, onPostDeleted }) => {
  const { user } = useSelector(state => state.auth);
  // const navigate = useNavigate();
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPost, setCurrentPost] = useState(post);
  const [showMenu, setShowMenu] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isReposted, setIsReposted] = useState(false);
  const token = localStorage.getItem('token');

  // Sync currentPost with post prop from Redux
  useEffect(() => {
    setCurrentPost(post);
  }, [post]);

  const isAuthor = user?._id === post.author._id;

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (commentText.trim()) {
      onComment(post._id, commentText);
      setCommentText('');
      setShowCommentForm(false);
    }
  };

  const handlePostUpdate = (updatedPost) => {
    setCurrentPost(updatedPost);
    if (onPostUpdate) {
      onPostUpdate(updatedPost);
    }
    setShowEditModal(false);
  };

  const handleDeletePost = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/posts/${post._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (onPostDeleted) {
          onPostDeleted();
        } else {
          window.location.reload();
        }
      } catch (err) {
        console.error('Error deleting post:', err);
        alert('Error deleting post');
      }
    }
  };

  const handleAdminDeletePost = async () => {
    if (window.confirm('Admin: Delete this post?')) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/admin/post/${post._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (onPostDeleted) {
          onPostDeleted();
        } else {
          window.location.reload();
        }
      } catch (err) {
        console.error('Error deleting post as admin:', err);
        alert('Error deleting post');
      }
    }
  };

  const handleSavePost = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/users/posts/${post._id}/save`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsSaved(response.data.isSaved);
    } catch (err) {
      console.error('Error saving post:', err);
    }
  };

  const handleRepost = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/users/posts/${post._id}/repost`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsReposted(response.data.isReposted);
      alert(response.data.message);
    } catch (err) {
      console.error('Error reposting:', err);
      alert('Error reposting');
    }
  };

  return (
    <div className="post-card">
      <div className="post-header">
        <Link to={`/profile/${post.author._id}`} className="author-link">
          <div className="author-avatar">
            {post.author.profilePicture ? (
              <img 
                src={`http://localhost:5000${post.author.profilePicture}`} 
                alt={post.author.username}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                {post.author.firstName?.charAt(0)}{post.author.lastName?.charAt(0)}
              </span>
            )}
          </div>
          <div className="author-info">
            <h3 style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
              {post.author.icon && (
                <img 
                  src={`http://localhost:5000${post.author.icon}`} 
                  alt="icon"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    backgroundColor: 'transparent'
                  }}
                />
              )}
              <span style={{color: post.author.nicknameColor || '#ffffff'}}>
                {post.author.firstName} {post.author.lastName}
              </span>
              {post.author.badge && <span style={{fontSize: '1rem'}}>{post.author.badge}</span>}
              {post.author.isVerified && <span className="verified-badge">✅</span>}
            </h3>
            <p>@{post.author.username}</p>
          </div>
        </Link>
        <div className="post-header-right">
          <span className="post-time">
            {new Date(post.createdAt).toLocaleDateString()}
            {currentPost.isEdited && <span className="edited-badge">Edited</span>}
          </span>
          {(isAuthor || user?.isAdmin) && (
            <div className="post-menu">
              <button className="menu-btn" onClick={() => setShowMenu(!showMenu)}>⋮</button>
              {showMenu && (
                <div className="dropdown-menu">
                  {isAuthor && (
                    <>
                      <button onClick={() => { setShowEditModal(true); setShowMenu(false); }}>
                        ✏️ Edit
                      </button>
                      <button onClick={handleDeletePost} className="delete-option">
                        🗑️ Delete
                      </button>
                    </>
                  )}
                  {user?.isAdmin && !isAuthor && (
                    <button onClick={handleAdminDeletePost} className="delete-option" style={{color: '#ff6b6b'}}>
                      🛡️ Admin Delete
                    </button>
                  )}
                  {user?.isAdmin && isAuthor && (
                    <button onClick={handleAdminDeletePost} className="delete-option" style={{color: '#ff6b6b'}}>
                      🛡️ Delete (Admin)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="post-content">
        <RichText content={currentPost.content} />
        {currentPost.image && (
          <div className="post-image">
            <img 
              src={`http://localhost:5000${currentPost.image}`} 
              alt="Post content"
            />
          </div>
        )}
      </div>

      <div className="post-stats">
        <span>❤️ {currentPost.likes.length}</span>
        <span>💬 {currentPost.comments.length}</span>
      </div>

      <div className="post-actions">
        <button className="action-btn like-btn" onClick={() => onLike(post._id)}>
          ❤️ Like
        </button>
        <button className="action-btn comment-btn" onClick={() => setShowCommentForm(!showCommentForm)}>
          💬 Comment
        </button>
        <button 
          className={`action-btn repost-btn ${isReposted ? 'reposted' : ''}`}
          onClick={handleRepost}
          title={isReposted ? 'Remove repost' : 'Repost'}
        >
          {isReposted ? '✔️' : '🔄'} Repost
        </button>
        <button 
          className={`action-btn save-btn ${isSaved ? 'saved' : ''}`} 
          onClick={handleSavePost}
          title={isSaved ? 'Unsave' : 'Save'}
        >
          {isSaved ? '🔖' : '🔓'} Save
        </button>
      </div>

      {showCommentForm && (
        <form className="comment-form" onSubmit={handleCommentSubmit}>
          <input
            type="text"
            placeholder="Add a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            required
          />
          <button type="submit">Comment</button>
        </form>
      )}

      {currentPost.comments.length > 0 && (
        <div className="comments-section">
          <h4>Comments ({currentPost.comments.length})</h4>
          {currentPost.comments.map((comment, index) => (
            <div key={index} className="comment">
              <div className="comment-header">
                <div className="comment-avatar">
                  {comment.user.profilePicture ? (
                    <img 
                      src={`http://localhost:5000${comment.user.profilePicture}`} 
                      alt={comment.user.username}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span>{comment.user.firstName?.charAt(0)}{comment.user.lastName?.charAt(0)}</span>
                  )}
                </div>
                <div>
                  <strong>
                    {comment.user.firstName} {comment.user.lastName}
                    {comment.user.isVerified && <span className="verified-badge-small">✅</span>}
                  </strong>
                  <p className="comment-user-handle">@{comment.user.username}</p>
                </div>
              </div>
              <p className="comment-text">{comment.text}</p>
            </div>
          ))}
        </div>
      )}

      {showEditModal && (
        <EditPostModal
          post={currentPost}
          onClose={() => setShowEditModal(false)}
          onSave={handlePostUpdate}
        />
      )}
    </div>
  );
};

export default PostCard;
